import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Check,
  CreditCard,
  Download,
  FileText,
  RefreshCw,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";

import api from "../api/client";
import { trackProductEvent } from "../analytics";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import type { PublicPlan } from "../components/PricingTable";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type { Organization } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate } from "../utils/format";

type Subscription = {
  id: number;
  plan_code: string;
  plan_name: string;
  package: string;
  billing_interval: "monthly" | "yearly";
  price: string;
  currency: string;
  status: string;
  current_period_end: string | null;
  trial_end: string | null;
  grace_period_end: string | null;
  cancel_at_period_end: boolean;
  scheduled_plan_code: string | null;
  seat_limit: number | null;
  active_seats: number;
};
type Payment = {
  id: number;
  provider_payment_id: string;
  plan_name: string;
  status: string;
  amount: string;
  currency: string;
  context: string;
  paid_at: string | null;
  created_at: string;
};
type BillingProfile = {
  customer_type: "consumer" | "business";
  contact_name: string;
  company_name: string;
  invoice_email: string;
  address_line1: string;
  postal_code: string;
  city: string;
  country_code: "NL";
  chamber_of_commerce_number: string;
  vat_number: string;
};
type Invoice = {
  id: number;
  kind: "invoice" | "credit_note";
  document_number: string;
  original_document_number: string | null;
  issued_at: string;
  plan_name: string;
  amount_including_vat: string;
  currency: string;
  download_url: string;
};
type RefundRecord = {
  id: number;
  amount: string;
  currency: string;
  reason: string;
  status: "pending" | "processing" | "refunded" | "failed";
  requested_at: string;
  processed_at: string | null;
  credit_document_number: string | null;
};
type Overview = {
  subscription: Subscription;
  payment_method_configured: boolean;
  payments: Payment[];
  billing_profile: BillingProfile | null;
  invoices: Invoice[];
  refunds: RefundRecord[];
  refund_guarantee: {
    available: boolean;
    deadline: string | null;
  };
};
type CheckoutQuote = {
  action: "checkout" | "trial_mandate" | "upgrade" | "downgrade" | "current";
  charge_now: string;
  recurring_price: string;
  currency: string;
  billing_interval: "monthly" | "yearly";
  price_includes_vat: boolean;
  vat_rate: string;
};

const emptyBillingProfile = (
  email = "",
  companyName = "",
  contactName = "",
): BillingProfile => ({
  customer_type: "business",
  contact_name: contactName,
  company_name: companyName,
  invoice_email: email,
  address_line1: "",
  postal_code: "",
  city: "",
  country_code: "NL",
  chamber_of_commerce_number: "",
  vat_number: "",
});

export default function SubscriptionPage() {
  const { user } = useAuth();
  const {
    organizationId,
    organization,
    reload: reloadWorkspace,
    selectOrganization,
  } = useWorkspace();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [organizationName, setOrganizationName] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("team");
  const [password, setPassword] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [checkoutDraft, setCheckoutDraft] = useState<{
    organization: number;
    planCode: string;
    idempotencyKey: string;
    trialMandate: boolean;
    quote: CheckoutQuote;
  } | null>(null);
  const [billingProfile, setBillingProfile] = useState<BillingProfile>(
    emptyBillingProfile(
      user?.email,
      "",
      `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
    ),
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canManageBilling =
    organization?.role === "owner" || organization?.role === "org_admin";
  const paidPlans = plans.filter((plan) => plan.package !== "free");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const plansResponse = await api.get<PublicPlan[]>("/billing/plans/");
      setPlans(plansResponse.data);
      if (plansResponse.data.length) {
        setSelectedPlan((current) =>
          plansResponse.data.some((plan) => plan.code === current)
            ? current
            : plansResponse.data.find((plan) => plan.package !== "free")?.code ??
              plansResponse.data[0].code,
        );
      }
      if (organizationId && canManageBilling) {
        const response = await api.get<Overview>(
          `/billing/subscription/?organization=${organizationId}`,
        );
        setOverview(response.data);
      } else setOverview(null);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De abonnementsgegevens konden niet worden geladen.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, canManageBilling]);
  useEffect(() => {
    void load();
  }, [load]);

  const openCheckout = async (
    planCode: string,
    targetOrganization = organizationId,
    targetOrganizationName = organization?.name ?? "",
  ) => {
    if (!targetOrganization) return;
    setWorking(true);
    setError("");
    try {
      const quoteResponse = await api.get<CheckoutQuote>("/billing/checkout/quote/", {
        params: {
          organization: targetOrganization,
          plan_code: planCode,
          billing_interval: "monthly",
        },
      });
      setBillingProfile(
        overview?.billing_profile ??
          emptyBillingProfile(
            user?.email,
            targetOrganizationName,
            `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          ),
      );
      setAcceptedTerms(false);
      setCheckoutDraft({
        organization: targetOrganization,
        planCode,
        idempotencyKey: crypto.randomUUID(),
        trialMandate: quoteResponse.data.action === "trial_mandate",
        quote: quoteResponse.data,
      });
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De prijsopgave kon niet worden geladen."),
      );
    } finally {
      setWorking(false);
    }
  };

  const checkout = async (event: FormEvent) => {
    event.preventDefault();
    if (!checkoutDraft) return;
    setWorking(true);
    setError("");
    try {
      const currentPackage = overview?.subscription.package;
      const target = plans.find((plan) => plan.code === checkoutDraft.planCode);
      const response = await api.post("/billing/checkout/", {
        organization: checkoutDraft.organization,
        plan_code: checkoutDraft.planCode,
        billing_interval: "monthly",
        idempotency_key: checkoutDraft.idempotencyKey,
        quoted_amount: checkoutDraft.quote.charge_now,
        billing_profile: billingProfile,
        accepted_terms: acceptedTerms,
      });
      trackProductEvent(
        currentPackage && target?.package === "team" && currentPackage === "business"
          ? "downgrade"
          : currentPackage
          ? "upgrade"
          : "checkout_started",
      );
      if (response.data.checkout_url)
        window.location.assign(response.data.checkout_url);
      else {
        setCheckoutDraft(null);
        setSuccess(
          response.data.action === "downgrade"
            ? "De downgrade staat klaar voor de volgende periode."
            : "Het pakket is bijgewerkt.",
        );
        await reloadWorkspace();
        await load();
      }
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De checkout kon niet worden gestart."));
    } finally {
      setWorking(false);
    }
  };

  const createOrganization = async (event: FormEvent) => {
    event.preventDefault();
    setWorking(true);
    setError("");
    try {
      const response = await api.post<Organization>("/organizations/", {
        name: organizationName,
        plan_code: selectedPlan,
      });
      selectOrganization(response.data.id);
      await reloadWorkspace();
      await openCheckout(selectedPlan, response.data.id, response.data.name);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De organisatie kon niet worden aangemaakt."),
      );
      setWorking(false);
    }
  };

  const cancel = async (event: FormEvent) => {
    event.preventDefault();
    if (!organizationId) return;
    setWorking(true);
    setError("");
    try {
      const response = await api.post("/billing/cancel/", {
        organization: organizationId,
        password,
      });
      setShowCancel(false);
      setPassword("");
      setSuccess(
        response.data.refund_applied
          ? "Het abonnement is direct gestopt. De volledige terugbetaling is verwerkt of aangevraagd; de creditfactuur staat bij Facturen."
          : "Het abonnement stopt aan het einde van de betaalperiode. Persoonlijke data blijft behouden.",
      );
      await reloadWorkspace();
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Opzeggen is niet gelukt."));
    } finally {
      setWorking(false);
    }
  };

  const reactivate = async () => {
    if (!organizationId) return;
    setWorking(true);
    try {
      await api.post("/billing/reactivate/", { organization: organizationId });
      setSuccess("Het abonnement is opnieuw geactiveerd.");
      await reloadWorkspace();
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Heractiveren is niet gelukt."));
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <LoadingState label="Abonnement laden..." />;
  if (!organizationId || !organization)
    return (
      <div className="subscription-page">
        <PageHeader
          title="Abonnement"
          description="Maak een organisatie aan om Team of Business te gebruiken."
        />
        {error && <ErrorNotice>{error}</ErrorNotice>}
        <div className="subscription-create-layout">
          <form className="surface settings-section" onSubmit={createOrganization}>
            <span className="settings-large-icon">
              <Building2 size={25} />
            </span>
            <div className="settings-heading">
              <h2>Organisatiewerkruimte maken</h2>
              <p>Je persoonlijke verlofkaart blijft daarnaast gratis beschikbaar.</p>
            </div>
            <label>
              <span>Organisatienaam</span>
              <input
                value={organizationName}
                onChange={(event) => setOrganizationName(event.target.value)}
                required
              />
            </label>
            <fieldset className="subscription-plan-options">
              <legend>Pakket</legend>
              {paidPlans.map((plan) => (
                <button
                  key={plan.code}
                  className={selectedPlan === plan.code ? "selected" : ""}
                  type="button"
                  role="radio"
                  aria-checked={selectedPlan === plan.code}
                  onClick={() => setSelectedPlan(plan.code)}
                >
                  <span>
                    <strong>{plan.name}</strong>
                    <small>
                      {plan.contact_sales
                        ? "Prijs op aanvraag"
                        : plan.monthly_price
                        ? `${new Intl.NumberFormat("nl-NL", {
                            style: "currency",
                            currency: plan.currency,
                          }).format(Number(plan.monthly_price))} per maand`
                        : "Prijs configureerbaar"}
                    </small>
                  </span>
                  <span className="subscription-plan-seat">
                    {plan.seat_limit ? `${plan.seat_limit} gebruiker` : "Team"}
                  </span>
                </button>
              ))}
            </fieldset>
            <button className="button primary" type="submit" disabled={working}>
              {working ? "Aanmaken..." : "Doorgaan naar betaling"}
              <ArrowRight size={17} />
            </button>
          </form>
          <section className="subscription-benefits">
            <h2>Na activatie beschikbaar</h2>
            <p>
              <Check size={17} /> Medewerkers en teams
            </p>
            <p>
              <Check size={17} /> Aanvragen en fiattering
            </p>
            <p>
              <Check size={17} /> Bezettingsinzicht
            </p>
            <p>
              <Check size={17} /> Organisatierapportages
            </p>
          </section>
        </div>
      </div>
    );
  if (!canManageBilling)
    return (
      <div>
        <PageHeader
          eyebrow={organization.name}
          title="Abonnement"
          description="Alleen een eigenaar of organisatiebeheerder kan betaalgegevens beheren."
        />
        <ErrorNotice>
          Je kunt het pakket en de status wel herkennen aan de werkruimtekiezer, maar
          niet wijzigen.
        </ErrorNotice>
      </div>
    );
  if (!overview)
    return (
      <div>
        <PageHeader title="Abonnement" />
        <ErrorNotice>Er is geen abonnementsrecord gevonden.</ErrorNotice>
      </div>
    );

  const subscription = overview.subscription;
  const checkoutPlan = plans.find((plan) => plan.code === checkoutDraft?.planCode);
  const refundFailed = overview.refunds.some((refund) => refund.status === "failed");
  const refundProcessing = overview.refunds.some((refund) =>
    ["pending", "processing"].includes(refund.status),
  );
  const refundCompleted =
    overview.refunds.length > 0 &&
    overview.refunds.every((refund) => refund.status === "refunded");
  const refundTotal = overview.refunds.reduce(
    (total, refund) => total + Number(refund.amount),
    0,
  );
  return (
    <div className="subscription-page">
      <PageHeader
        eyebrow={organization.name}
        title="Abonnement"
        description="Pakket, seats, betalingen en wijzigingen op één plek."
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <section className="subscription-summary surface">
        <div className="plan-summary">
          <span className="settings-large-icon">
            <CreditCard size={24} />
          </span>
          <div>
            <p className="eyebrow">Huidig pakket</p>
            <h2>{subscription.plan_name}</h2>
            <p className={`subscription-state status-${subscription.status}`}>
              {subscription.status.replace("_", " ")}
            </p>
          </div>
        </div>
        <div className="subscription-facts">
          <div>
            <span>Prijs</span>
            <strong>
              {new Intl.NumberFormat("nl-NL", {
                style: "currency",
                currency: subscription.currency,
              }).format(Number(subscription.price))}
              <small>/ maand</small>
            </strong>
            <small>inclusief 21% btw</small>
          </div>
          <div>
            <span>Volgende datum</span>
            <strong>
              {subscription.current_period_end
                ? formatDate(subscription.current_period_end)
                : "Na activatie"}
            </strong>
          </div>
          <div>
            <span>Seats</span>
            <strong>
              {subscription.active_seats} / {subscription.seat_limit ?? "∞"}
            </strong>
          </div>
          <div>
            <span>Betaalmethode</span>
            <strong>
              {overview.payment_method_configured ? "Ingesteld" : "Nog niet ingesteld"}
            </strong>
          </div>
        </div>
        {subscription.cancel_at_period_end && (
          <div className="notice notice-warning compact">
            <AlertTriangle size={18} />
            <span>
              Het abonnement eindigt op{" "}
              {subscription.current_period_end
                ? formatDate(subscription.current_period_end)
                : "de einddatum"}
              .
            </span>
            <button
              className="button subtle small"
              onClick={() => void reactivate()}
              disabled={working}
            >
              <RefreshCw size={15} /> Heractiveren
            </button>
          </div>
        )}
      </section>
      <section className="surface settings-section">
        <div className="settings-heading">
          <h2>Pakket wijzigen</h2>
          <p>
            Upgrades gaan na betaling in. Downgrades worden voor de volgende periode
            ingepland.
          </p>
        </div>
        <div className="package-switch-grid">
          {plans
            .filter((plan) => plan.package !== "free")
            .map((plan) => {
              const isCurrent = plan.code === subscription.plan_code;
              const needsPaymentMethod =
                isCurrent &&
                subscription.status === "trialing" &&
                !overview.payment_method_configured;
              return (
                <article key={plan.code} className={isCurrent ? "current" : ""}>
                  <div>
                    <strong>{plan.name}</strong>
                    <span>
                      {plan.contact_sales
                        ? "Prijs op aanvraag"
                        : plan.monthly_price
                        ? `${new Intl.NumberFormat("nl-NL", {
                            style: "currency",
                            currency: plan.currency,
                          }).format(Number(plan.monthly_price))}/maand incl. 21% btw`
                        : "Configureerbaar"}
                    </span>
                  </div>
                  <button
                    className="button subtle small"
                    type="button"
                    disabled={
                      working ||
                      (isCurrent && !needsPaymentMethod) ||
                      plan.contact_sales
                    }
                    onClick={() => void openCheckout(plan.code)}
                  >
                    {needsPaymentMethod
                      ? "Betaalmethode instellen"
                      : isCurrent
                      ? "Huidig"
                      : "Kiezen"}
                  </button>
                </article>
              );
            })}
        </div>
        {subscription.scheduled_plan_code && (
          <p className="scheduled-change">
            Ingepland pakket: <strong>{subscription.scheduled_plan_code}</strong>
          </p>
        )}
      </section>
      <section className="surface settings-section">
        <div className="settings-heading">
          <h2>Betalingen</h2>
          <p>
            Mollie verwerkt betaalgegevens; DeVerlofkaart bewaart facturen,
            providerreferenties en statussen.
          </p>
        </div>
        <div className="payment-list">
          {overview.payments.length === 0 && (
            <p className="empty-copy">Nog geen betalingen beschikbaar.</p>
          )}
          {overview.payments.map((payment) => (
            <article key={payment.id}>
              <span className="payment-icon">
                <FileText size={18} />
              </span>
              <span>
                <strong>{payment.plan_name}</strong>
                <small>
                  {formatDate(payment.created_at)} · {payment.provider_payment_id}
                </small>
              </span>
              <strong>
                {new Intl.NumberFormat("nl-NL", {
                  style: "currency",
                  currency: payment.currency,
                }).format(Number(payment.amount))}
              </strong>
              <span className={`member-status status-${payment.status}`}>
                {payment.status}
              </span>
            </article>
          ))}
        </div>
      </section>
      <section className="surface settings-section">
        <div className="settings-heading">
          <h2>Facturen</h2>
          <p>Bedragen zijn inclusief 21% btw.</p>
        </div>
        <div className="payment-list">
          {overview.invoices.length === 0 && (
            <p className="empty-copy">Nog geen facturen beschikbaar.</p>
          )}
          {overview.invoices.map((invoice) => (
            <article key={invoice.id}>
              <span className="payment-icon">
                <FileText size={18} />
              </span>
              <span>
                <strong>{invoice.document_number}</strong>
                <small>
                  {invoice.kind === "credit_note" ? "Creditfactuur" : "Factuur"} ·{" "}
                  {formatDate(invoice.issued_at)}
                </small>
              </span>
              <strong>
                {new Intl.NumberFormat("nl-NL", {
                  style: "currency",
                  currency: invoice.currency,
                }).format(Number(invoice.amount_including_vat))}
              </strong>
              <a
                className="icon-button"
                href={invoice.download_url}
                title={`${invoice.document_number} downloaden`}
                aria-label={`${invoice.document_number} downloaden`}
              >
                <Download size={17} />
              </a>
            </article>
          ))}
        </div>
      </section>
      {overview.refunds.length > 0 && (
        <section
          className={`notice compact ${
            refundFailed ? "notice-warning" : "notice-info"
          }`}
        >
          {refundFailed ? <AlertTriangle size={18} /> : <RefreshCw size={18} />}
          <span>
            <strong>
              {refundFailed
                ? "Terugbetaling vereist opnieuw proberen"
                : refundCompleted
                ? "Terugbetaling afgerond"
                : "Terugbetaling in behandeling"}
            </strong>{" "}
            {new Intl.NumberFormat("nl-NL", {
              style: "currency",
              currency: overview.refunds[0].currency,
            }).format(refundTotal)}
            {overview.refunds[0].credit_document_number
              ? ` · ${overview.refunds[0].credit_document_number}`
              : ""}
          </span>
        </section>
      )}
      {!refundProcessing &&
        !refundCompleted &&
        (refundFailed ||
          (!subscription.cancel_at_period_end &&
            !["canceled", "expired"].includes(subscription.status))) && (
          <section className="danger-zone">
            <div>
              <h2>
                {refundFailed
                  ? "Terugbetaling opnieuw proberen"
                  : "Abonnement opzeggen"}
              </h2>
              <p>
                {refundFailed
                  ? "De eerdere providerpoging is niet afgerond. Bevestig opnieuw om dezelfde terugbetaling veilig te hervatten."
                  : overview.refund_guarantee.available
                  ? `Je ontvangt het volledige betaalde bedrag terug wanneer je nu opzegt${
                      overview.refund_guarantee.deadline
                        ? `, uiterlijk tot ${formatDate(
                            overview.refund_guarantee.deadline,
                          )}`
                        : ""
                    }.`
                  : "Organisatiegegevens blijven bestaan en worden alleen-lezen. Je persoonlijke DeVerlofkaart blijft beschikbaar."}
              </p>
            </div>
            <button
              className="button danger"
              type="button"
              onClick={() => setShowCancel(true)}
            >
              {refundFailed ? <RefreshCw size={17} /> : <X size={17} />}
              {refundFailed ? "Opnieuw proberen" : "Opzeggen"}
            </button>
          </section>
        )}
      {checkoutDraft && checkoutPlan && (
        <div className="modal-backdrop" role="presentation">
          <form
            className="modal-panel checkout-modal"
            onSubmit={checkout}
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-title"
          >
            <div className="checkout-modal-header">
              <div>
                <p className="eyebrow">{checkoutPlan.name}</p>
                <h2 id="checkout-title">Factuurgegevens</h2>
              </div>
              <strong>
                {new Intl.NumberFormat("nl-NL", {
                  style: "currency",
                  currency: checkoutDraft.quote.currency,
                }).format(Number(checkoutDraft.quote.charge_now))}
                <small>nu te betalen, inclusief 21% btw</small>
              </strong>
            </div>
            <div className="checkout-order-summary">
              {checkoutDraft.trialMandate ? (
                <p>
                  <strong>{checkoutPlan.trial_days} dagen proefperiode.</strong> Het
                  bedrag van{" "}
                  {new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: checkoutDraft.quote.currency,
                  }).format(Number(checkoutDraft.quote.charge_now))}{" "}
                  is een eenmalige machtigingsbetaling. Daarna wordt{" "}
                  {new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: checkoutDraft.quote.currency,
                  }).format(Number(checkoutDraft.quote.recurring_price))}{" "}
                  per maand inclusief 21% btw geïncasseerd.
                </p>
              ) : (
                <p>
                  <strong>Bedrag van deze bestelling:</strong>{" "}
                  {new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: checkoutDraft.quote.currency,
                  }).format(Number(checkoutDraft.quote.charge_now))}{" "}
                  inclusief 21% btw. Daarna kost het pakket{" "}
                  {new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: checkoutDraft.quote.currency,
                  }).format(Number(checkoutDraft.quote.recurring_price))}{" "}
                  per maand inclusief 21% btw.
                </p>
              )}
              <p>
                Het abonnement loopt voor onbepaalde tijd, wordt maandelijks verlengd en
                is via deze pagina opzegbaar tegen het einde van de lopende
                betaalperiode.
              </p>
            </div>
            <div
              className="segmented-control billing-customer-type"
              role="group"
              aria-label="Klanttype"
            >
              <button
                type="button"
                className={billingProfile.customer_type === "consumer" ? "active" : ""}
                onClick={() =>
                  setBillingProfile({
                    ...billingProfile,
                    customer_type: "consumer",
                    company_name: "",
                    chamber_of_commerce_number: "",
                    vat_number: "",
                  })
                }
              >
                Particulier
              </button>
              <button
                type="button"
                className={billingProfile.customer_type === "business" ? "active" : ""}
                onClick={() =>
                  setBillingProfile({
                    ...billingProfile,
                    customer_type: "business",
                    company_name: organization.name,
                  })
                }
              >
                Zakelijk
              </button>
            </div>
            <div className="form-row">
              <label>
                <span>Contactpersoon</span>
                <input
                  autoComplete="name"
                  value={billingProfile.contact_name}
                  onChange={(event) =>
                    setBillingProfile({
                      ...billingProfile,
                      contact_name: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                <span>Factuurmail</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={billingProfile.invoice_email}
                  onChange={(event) =>
                    setBillingProfile({
                      ...billingProfile,
                      invoice_email: event.target.value,
                    })
                  }
                  required
                />
              </label>
            </div>
            {billingProfile.customer_type === "business" && (
              <label>
                <span>Bedrijfsnaam</span>
                <input
                  autoComplete="organization"
                  value={billingProfile.company_name}
                  onChange={(event) =>
                    setBillingProfile({
                      ...billingProfile,
                      company_name: event.target.value,
                    })
                  }
                  required
                />
              </label>
            )}
            <label>
              <span>Adres</span>
              <input
                autoComplete="street-address"
                value={billingProfile.address_line1}
                onChange={(event) =>
                  setBillingProfile({
                    ...billingProfile,
                    address_line1: event.target.value,
                  })
                }
                required
              />
            </label>
            <div className="billing-address-row">
              <label>
                <span>Postcode</span>
                <input
                  autoComplete="postal-code"
                  value={billingProfile.postal_code}
                  onChange={(event) =>
                    setBillingProfile({
                      ...billingProfile,
                      postal_code: event.target.value.toUpperCase(),
                    })
                  }
                  placeholder="1234 AB"
                  required
                />
              </label>
              <label>
                <span>Plaats</span>
                <input
                  autoComplete="address-level2"
                  value={billingProfile.city}
                  onChange={(event) =>
                    setBillingProfile({ ...billingProfile, city: event.target.value })
                  }
                  required
                />
              </label>
              <label>
                <span>Land</span>
                <select value="NL" disabled aria-label="Land">
                  <option value="NL">Nederland</option>
                </select>
              </label>
            </div>
            {billingProfile.customer_type === "business" && (
              <div className="form-row">
                <label>
                  <span>KvK-nummer (optioneel)</span>
                  <input
                    inputMode="numeric"
                    value={billingProfile.chamber_of_commerce_number}
                    onChange={(event) =>
                      setBillingProfile({
                        ...billingProfile,
                        chamber_of_commerce_number: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  <span>Btw-nummer (optioneel)</span>
                  <input
                    value={billingProfile.vat_number}
                    onChange={(event) =>
                      setBillingProfile({
                        ...billingProfile,
                        vat_number: event.target.value.toUpperCase(),
                      })
                    }
                  />
                </label>
              </div>
            )}
            <label className="check-field checkout-terms">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                required
              />
              <span>
                Ik ga akkoord met de{" "}
                <a href="/algemene-voorwaarden" target="_blank" rel="noreferrer">
                  Algemene voorwaarden
                </a>{" "}
                en bevestig mijn bestelling. De eerste betaalde periode heeft een
                volledige 14-dagen-terugbetalingsgarantie.
              </span>
            </label>
            <div className="modal-actions">
              <button
                className="button subtle"
                type="button"
                onClick={() => setCheckoutDraft(null)}
              >
                Annuleren
              </button>
              <button
                className="button primary"
                type="submit"
                disabled={working || !acceptedTerms}
              >
                {working
                  ? "Bestelling starten..."
                  : Number(checkoutDraft.quote.charge_now) > 0
                  ? "Bestellen en betalen"
                  : "Bestelling bevestigen"}
                <ArrowRight size={17} />
              </button>
            </div>
          </form>
        </div>
      )}
      {showCancel && (
        <div className="modal-backdrop" role="presentation">
          <form
            className="modal-panel"
            onSubmit={cancel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-title"
          >
            <h2 id="cancel-title">
              {refundFailed ? "Terugbetaling opnieuw proberen" : "Abonnement opzeggen"}
            </h2>
            <p>
              {refundFailed
                ? "Bevestig met je wachtwoord om de volledige terugbetaling opnieuw bij Mollie aan te vragen."
                : overview.refund_guarantee.available
                ? "Bevestig met je wachtwoord. Het abonnement stopt direct en het volledige betaalde bedrag wordt terugbetaald."
                : "Bevestig met je wachtwoord. De opzegging gaat in aan het einde van de huidige periode."}
            </p>
            <label>
              <span>Wachtwoord</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoFocus
              />
            </label>
            <div className="modal-actions">
              <button
                className="button subtle"
                type="button"
                onClick={() => setShowCancel(false)}
              >
                Annuleren
              </button>
              <button className="button danger" type="submit" disabled={working}>
                {refundFailed ? "Terugbetaling hervatten" : "Opzegging bevestigen"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
